import { type RefObject, useEffect, useMemo, useRef } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

/**
 * Small, dependency-free deep equal using a stable stringify.
 * Good enough for default-values snapshots; replace with fast-deep-equal
 * if you prefer.
 */
function stableStringify(value: unknown): string {
  const seen = new WeakSet();

  const stringify = (v: any): any => {
    if (v && typeof v === "object") {
      if (seen.has(v)) return "[[circular]]";
      seen.add(v);

      if (Array.isArray(v)) return v.map(stringify);

      const keys = Object.keys(v).sort();
      const out: Record<string, any> = {};
      for (const k of keys) out[k] = stringify(v[k]);
      return out;
    }
    return v;
  };

  return JSON.stringify(stringify(value));
}

// Module-level constant — stable reference, never triggers effect re-runs.
const defaultEquals = <T>(a: T, b: T): boolean =>
  stableStringify(a) === stableStringify(b);

type ResetOptions<T extends FieldValues> = Parameters<
  UseFormReturn<T>["reset"]
>[1];

export type UseDefaultValuesOptions<T extends FieldValues> = {
  /**
   * Custom comparator if you don't want JSON-based deep compare.
   */
  isEqual?: (a: T, b: T) => boolean;
  /**
   * react-hook-form reset options
   */
  resetOptions?: ResetOptions<T>;
};

/**
 * useDefaultValues
 *
 * 1) Computes and memoizes default values from a builder function + deps.
 * 2) Returns the computed value on initial render so it can be used in useForm({ defaultValues }).
 * 3) If a formRef is provided (pointing at a react-hook-form instance), it will call form.reset
 *    when the computed default values change (deep compare).
 *
 * Usage pattern:
 *   const formRef = useRef<UseFormReturn<FormValues> | null>(null);
 *   const defaults = useDefaultValues<FormValues>(() => buildDefaults(props), [deps...], formRef);
 *   const form = useForm<FormValues>({ defaultValues: defaults });
 *   formRef.current = form; // safe to assign during render
 */
export function useDefaultValues<T extends FieldValues>(
  builder: () => T,
  deps: React.DependencyList,
  formRef?: RefObject<Pick<UseFormReturn<any>, "reset"> | null>,
  options?: UseDefaultValuesOptions<T>,
): T {
  const { isEqual, resetOptions } = options ?? {};

  // Hold the latest equals/resetOptions in refs so the effect never needs them
  // as deps — changing them won't trigger an unnecessary reset.
  const equalsRef = useRef(isEqual ?? defaultEquals<T>);
  equalsRef.current = isEqual ?? defaultEquals<T>;

  const resetOptionsRef = useRef(resetOptions);
  resetOptionsRef.current = resetOptions;

  // Memoized computed defaults based on deps
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are forwarded from the caller
  const computed = useMemo(builder, deps);

  // Keep the last applied snapshot for comparison
  const lastAppliedRef = useRef<T>(computed);

  // Only re-runs when `computed` or `formRef` changes — not on every render.
  useEffect(() => {
    const form = formRef?.current;
    if (!form) return;

    const prev = lastAppliedRef.current;
    if (!equalsRef.current(prev, computed)) {
      form.reset(computed as any, resetOptionsRef.current);
      lastAppliedRef.current = computed;
    }
  }, [computed, formRef]);

  return computed;
}
